import React, { useEffect, useState } from 'react'
import apiClient from '../../services/apiClient'
import { CBadge, CCard, CCardBody, CImage, CProgress, CSpinner } from '@coreui/react'
import BadgeComponent from './BadgeComponent'
import PropTypes from 'prop-types'
import { buildApiUrl, getPicture } from '../../utils/getConfiguration'
import { SplashScreen } from '@capacitor/splash-screen'
import { useTranslation } from 'react-i18next'

const hideSplashScreen = () => {
  if (typeof window === 'undefined') {
    return
  }

  const splash = document.getElementById('splash-screen')
  if (splash) splash.style.display = 'none'

  if (typeof SplashScreen?.hide === 'function') {
    SplashScreen.hide({ fadeOutDuration: 200 }).catch((splashError) => {
      console.warn('Unable to hide splash screen', splashError)
    })
    return
  }

  if (typeof window.SplashScreen?.hide === 'function') {
    window.SplashScreen.hide({ fadeOutDuration: 200 }).catch((splashError) => {
      console.warn('Unable to hide splash screen', splashError)
    })
  }
}

const createInitialWidgetData = () => ({
  top: [],
  damage: [],
  active: [],
  progress: [],
})

const normaliseArray = (payload, label) => {
  if (Array.isArray(payload)) {
    return payload
  }

  if (payload === undefined || payload === null) {
    return []
  }

  console.warn(`Expected array response for ${label}, received:`, payload)
  return []
}

const toNumeric = (value) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : 0
}

const calculateTotalRisk = (items = []) =>
  items.reduce((total, item) => total + toNumeric(item?.SUMPOINTS), 0)

const calculateAverageProgress = (items = []) => {
  if (!items.length) {
    return 0
  }

  const total = items.reduce((sum, item) => sum + toNumeric(item?.Progress), 0)
  return Math.round(total / items.length)
}

const WidgetStatisticAreaSum = ({ grp_ID }) => {
  const { t } = useTranslation()
  const [accountContext, setAccountContext] = useState({ accountId: null, gender: null })
  const { accountId, gender } = accountContext

  const [widgetData, setWidgetData] = useState(createInitialWidgetData)
  const [status, setStatus] = useState({ loading: false, error: null })
  const {
    top: topRecommendations,
    damage: accountDamages,
    active: activeRecommendations,
    progress: progressRecommendations,
  } = widgetData
  const { loading, error } = status
  const totalRisk = calculateTotalRisk(accountDamages)
  const averageProgress = calculateAverageProgress(progressRecommendations)
  const formattedRisk = totalRisk.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const readStorage = () => {
      const nextAccountId = window.localStorage.getItem('account_ID')
      const nextGender = window.localStorage.getItem('gender')

      setAccountContext((prev) => {
        if (prev.accountId === nextAccountId && prev.gender === nextGender) {
          return prev
        }

        return {
          accountId: nextAccountId,
          gender: nextGender,
        }
      })
    }

    readStorage()
    window.addEventListener('storage', readStorage)

    return () => {
      window.removeEventListener('storage', readStorage)
    }
  }, [])

  useEffect(() => {
    if (!accountId) {
      setWidgetData(createInitialWidgetData())
      setStatus({ loading: false, error: null })
      hideSplashScreen()
      return
    }

    const controller = new AbortController()

    setStatus({ loading: true, error: null })

    const endpoints = [
      {
        key: 'top',
        endpoint: 'remindersTop',
        params: { account_ID: accountId, grp_ID, gender: gender || undefined },
        logLabel: 'recommendations',
      },
      {
        key: 'damage',
        endpoint: 'accountDamage',
        params: { account_ID: accountId, grp_ID },
        logLabel: 'score',
      },
      {
        key: 'active',
        endpoint: 'accountReminders',
        params: { account_ID: accountId, grp_ID, gender: gender || undefined },
        logLabel: 'active recommendations',
      },
      {
        key: 'progress',
        endpoint: 'accountRemindersProgress',
        params: { account_ID: accountId, grp_ID },
        logLabel: 'progress recommendations',
      },
    ]

    const fetchData = async () => {
      try {
        // Fetch all widget datasets in parallel to minimise wait time.
        const requests = endpoints.map(({ endpoint, params }) =>
          apiClient.get(buildApiUrl(endpoint, params), {
            signal: controller.signal,
          }),
        )

        const results = await Promise.allSettled(requests)

        if (controller.signal.aborted) {
          return
        }

        const aggregatedData = createInitialWidgetData()
        let hasError = false

        results.forEach((result, index) => {
          const { key, logLabel } = endpoints[index]

          if (result.status === 'fulfilled') {
            aggregatedData[key] = normaliseArray(result.value?.data, logLabel)
            return
          }

          if (!apiClient.isCancel(result.reason)) {
            console.error(`Error fetching ${logLabel}`, result.reason)
            hasError = true
          }
          aggregatedData[key] = []
        })

        setWidgetData(aggregatedData)
        setStatus({
          loading: false,
          error: hasError ? 'Nepodarilo se nacist vsechna data widgetu.' : null,
        })
        hideSplashScreen()
      } catch (fetchError) {
        if (apiClient.isCancel(fetchError)) {
          return
        }

        console.error('Error fetching widget data', fetchError)
        if (!controller.signal.aborted) {
          setWidgetData(createInitialWidgetData())
          setStatus({ loading: false, error: t('widgets.statisticAreaSum.errors.load') })
          hideSplashScreen()
        }
      }
    }

    fetchData()

    return () => {
      controller.abort()
    }
  }, [accountId, gender, grp_ID, t])

  return (
    <>
      <CCard key={1} className="col-md-12 border border-light-subtle shadow-sm rounded-3 bg-white">
        <CCardBody className="p-2">
          <div className="d-flex flex-wrap align-items-center gap-1 mb-1 small">
            <div className="d-flex align-items-center gap-1 flex-grow-1 flex-sm-grow-0">
              <CImage
                rounded
                orientation="top"
                src={getPicture(grp_ID)}
                width={26}
                height={26}
                className="flex-shrink-0"
              />
              <div className="d-flex flex-wrap gap-1 align-items-center">
                {topRecommendations.length ? (
                  topRecommendations.map((topRecommendation, index) => (
                    <BadgeComponent
                      key={topRecommendation.ID ?? topRecommendation.Prio ?? index}
                      priority={topRecommendation.Prio}
                    />
                  ))
                ) : (
                  <CBadge color="success" size="sm">
                    {t('widgets.statisticAreaSum.badges.done')}
                  </CBadge>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center gap-1 text-nowrap">
              <span className="text-uppercase text-muted">
                {t('widgets.statisticAreaSum.labels.risk')}
              </span>
              <strong>{formattedRisk}$</strong>
            </div>
            <div className="d-flex align-items-center gap-1 ms-auto text-nowrap">
              {loading && (
                <>
                  <CSpinner size="sm" />
                  <span className="text-muted d-none d-sm-inline">
                    {t('widgets.statisticAreaSum.labels.loading')}
                  </span>
                </>
              )}
              {!loading && error && <span className="text-danger">{error}</span>}
            </div>
          </div>
          <div className="d-flex flex-wrap gap-1 small">
            <div className="d-flex align-items-center gap-1 text-nowrap">
              <span className="text-uppercase text-muted">
                {t('widgets.statisticAreaSum.labels.toFulfill')}
              </span>
              <CBadge color="info" size="sm">
                {activeRecommendations.length}
              </CBadge>
            </div>
            <div className="d-flex align-items-center gap-1 flex-grow-1">
              <span className="text-uppercase text-muted">
                {t('widgets.statisticAreaSum.labels.progress')}
              </span>
              <CProgress
                thin
                value={averageProgress}
                color="info"
                className="flex-grow-1"
                style={{ minWidth: '70px' }}
              />
              <span className="fw-semibold text-nowrap">{averageProgress}%</span>
            </div>
          </div>
        </CCardBody>
      </CCard>
    </>
  )
}

WidgetStatisticAreaSum.propTypes = {
  grp_ID: PropTypes.number.isRequired,
}

export default WidgetStatisticAreaSum
